import { useLingui } from '@lingui/react/macro';
import { useEffect, useState } from 'react';
import { decimalStringToBigInt, type TokenDetail } from '@/utils/tokenNumberUtils.ts';

export type ValidationErrorType = 'NONE' | 'MIN_AMOUNT' | 'MAX_AMOUNT' | 'INVALID_FORMAT';

interface TokenAmountValidationProps {
  inputValue: string;
  token?: TokenDetail;
  minAmount?: bigint;
  maxAmount?: { value: bigint; decimals: number };
  formattedMinAmount: string;
  balanceType?: 'wallet' | 'collateral';
}

export function useTokenAmountValidation({
  inputValue,
  token,
  minAmount,
  maxAmount,
  formattedMinAmount,
  balanceType = 'wallet',
}: TokenAmountValidationProps) {
  const { t } = useLingui();
  const [error, setError] = useState<{ message?: string; type: ValidationErrorType }>({ type: 'NONE' });
  const [isValid, setIsValid] = useState<boolean>(false);

  useEffect(() => {
    if (!token) {
      setError({ type: 'NONE' });
      setIsValid(false);
      return;
    }

    const trimmedInput = inputValue.trim();
    if (trimmedInput === '') {
      setError({ type: 'NONE' });
      setIsValid(false);
      return;
    }
    try {
      const bigAmount = decimalStringToBigInt(trimmedInput, token.decimals);
      if (minAmount != null && bigAmount < minAmount) {
        const tokenSymbol = token.symbol;
        setError({
          message: t`Amount must be at least ${formattedMinAmount} ${tokenSymbol}.`,
          type: 'MIN_AMOUNT',
        });
        setIsValid(false);
      } else if (maxAmount && bigAmount > maxAmount.value) {
        setError({
          message:
            balanceType === 'collateral' ? t`Amount exceeds your collateral balance.` : t`Amount exceeds your wallet balance.`,
          type: 'MAX_AMOUNT',
        });
        setIsValid(false);
      } else {
        setError({ type: 'NONE' });
        setIsValid(true);
      }
    } catch {
      setError({
        message: t`Please enter a valid amount.`,
        type: 'INVALID_FORMAT',
      });
      setIsValid(false);
    }
  }, [inputValue, minAmount, maxAmount, token, formattedMinAmount, balanceType, t]);

  return { error, isValid };
}
